/**
 * AdminArticleList — super-admin index of every article.
 *
 * Route:  /admin/articles
 * Auth:   is_admin required
 * Reads:  GET /api/articles?include_unpublished=1
 * Writes: navigates to /admin/articles/edit/:slug (or /articles/:slug for preview)
 *
 * Kept intentionally simple: sortable-by-updated-at grid, click a row to edit.
 * No delete button here on purpose — deletes are rare and destructive;
 * do them via the API if you must.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { toast } from "sonner";
import {
  ArrowLeft, Loader2, Search, Pencil, ExternalLink, FileText, PlusCircle,
} from "lucide-react";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

export default function AdminArticleList() {
  const nav = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const isAdmin = user?.is_admin;

  useEffect(() => {
    if (authLoading) return;
    if (!isAdmin) {
      toast.error("Admin access required");
      nav("/", { replace: true });
      return;
    }
    (async () => {
      try {
        const { data } = await http.get("/api/articles", { params: { include_unpublished: true } });
        const items = data.items || data || [];
        // Newest first
        items.sort((a, b) => (b.updated_at || "").localeCompare(a.updated_at || ""));
        setArticles(items);
      } catch (e) {
        toast.error(`Failed to load: ${e?.response?.data?.detail || e.message}`);
      } finally {
        setLoading(false);
      }
    })();
  }, [isAdmin, authLoading, nav]);

  const filtered = useMemo(() => {
    if (!q.trim()) return articles;
    const needle = q.trim().toLowerCase();
    return articles.filter((a) =>
      (a.title || "").toLowerCase().includes(needle) ||
      (a.slug || "").toLowerCase().includes(needle) ||
      (a.section || "").toLowerCase().includes(needle),
    );
  }, [articles, q]);

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50">
        <Loader2 className="w-6 h-6 animate-spin text-neutral-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50" data-testid="admin-article-list">
      <Helmet><title>Articles · Admin</title></Helmet>

      <div className="max-w-5xl mx-auto px-6 py-8">
        <Link to="/" className="text-neutral-500 hover:text-neutral-900 text-sm flex items-center gap-1 mb-4" data-testid="articles-back-btn">
          <ArrowLeft className="w-4 h-4" /> Back to site
        </Link>

        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-serif font-semibold text-neutral-900">Articles</h1>
            <p className="text-sm text-neutral-500 mt-1">
              {articles.length.toLocaleString()} total ·
              {' '}{articles.filter((a) => a.published !== false).length.toLocaleString()} published
            </p>
          </div>
          <a
            href="https://content-hub-1904.preview.emergentagent.com/api/handoff/ift-integration?html=1"
            target="_blank" rel="noreferrer"
            className="text-sm text-neutral-500 hover:text-neutral-900 flex items-center gap-1"
            title="Handoff doc"
          >
            <FileText className="w-4 h-4" /> Docs
          </a>
        </div>

        <div className="relative mb-6">
          <Search className="w-4 h-4 text-neutral-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search title, slug, section…"
            className="w-full pl-10 pr-4 py-2.5 border border-neutral-200 rounded-md bg-white text-sm"
            data-testid="articles-search"
          />
        </div>

        <div className="bg-white border border-neutral-200 rounded-lg overflow-hidden divide-y divide-neutral-100">
          {filtered.length === 0 && (
            <div className="p-12 text-center text-neutral-500 text-sm">
              {q ? `No articles match "${q}"` : "No articles yet."}
            </div>
          )}
          {filtered.map((a) => (
            <div
              key={a.slug}
              className="flex items-start gap-4 p-4 hover:bg-neutral-50 transition-colors"
              data-testid={`article-row-${a.slug}`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  {a.kicker && (
                    <span className="text-[10px] uppercase tracking-widest text-neutral-500 font-medium">
                      {a.kicker}
                    </span>
                  )}
                  {a.published === false && (
                    <span className="text-[10px] uppercase tracking-widest text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">
                      Draft
                    </span>
                  )}
                </div>
                <h3 className="font-serif font-semibold text-neutral-900 truncate">{a.title || a.slug}</h3>
                {a.subtitle && <p className="text-sm text-neutral-600 mt-0.5 line-clamp-1">{a.subtitle}</p>}
                <div className="flex items-center gap-3 mt-2 text-xs text-neutral-500">
                  <code className="text-[11px] bg-neutral-100 px-1.5 py-0.5 rounded">{a.slug}</code>
                  {a.section && <span>· {a.section}</span>}
                  {a.updated_at && (
                    <span>· updated {new Date(a.updated_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}</span>
                  )}
                  {a.read_minutes && <span>· {a.read_minutes} min read</span>}
                </div>
              </div>
              <div className="flex items-center gap-1">
                <a
                  href={`/articles/${a.slug}`}
                  target="_blank" rel="noreferrer"
                  className="text-neutral-500 hover:text-neutral-900 p-2 rounded hover:bg-neutral-100"
                  title="Open live"
                  data-testid={`article-preview-${a.slug}`}
                >
                  <ExternalLink className="w-4 h-4" />
                </a>
                <Link
                  to={`/admin/articles/edit/${a.slug}`}
                  className="text-neutral-500 hover:text-neutral-900 p-2 rounded hover:bg-neutral-100 flex items-center gap-1 text-sm"
                  data-testid={`article-edit-${a.slug}`}
                >
                  <Pencil className="w-4 h-4" /> Edit
                </Link>
              </div>
            </div>
          ))}
        </div>

        <p className="text-xs text-neutral-500 mt-6">
          To create a new article, use <code>POST /api/articles</code> (or add via the seeder script).
          A create-in-browser flow is not built yet — tell the operator if you'd like one.
        </p>
      </div>
    </div>
  );
}
